import { getAuth } from "firebase/auth";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";

interface WebSocketContextType {
  ws: WebSocket | null;
  sendMessage: (message: string) => void;
}

const MAX_RETRY_COUNT = 5;

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

export const WebSocketProvider: React.FC<{
  url: string;
  children: ReactNode;
}> = ({ url, children }) => {
  const [ws, setWs] = useState<WebSocket | null>(null);
  const retryCount = useRef(0);

  const getToken = useCallback(async () => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    return token || "";
  }, []);

  useEffect(() => {
    if (!url || url.includes("localhost")) {
      return;
    }

    let wsInstance: WebSocket | null = null;
    let heartbeat: NodeJS.Timeout;
    let isMounted = true;

    const connect = async () => {
      try {
        const token = await getToken();
        const newWs = new WebSocket(`${url}?token=${token}`);

        newWs.onopen = () => {
          if (!isMounted) return;
          retryCount.current = 0;
          heartbeat = setInterval(() => {
            if (newWs.readyState === WebSocket.OPEN) {
              newWs.send(JSON.stringify({ type: "ping" }));
            }
          }, 5000);
        };
        newWs.onclose = () => {
          if (!isMounted) return;
          clearInterval(heartbeat);
          if (retryCount.current < MAX_RETRY_COUNT) {
            retryCount.current++;
            setTimeout(connect, 5000);
          }
        };

        newWs.onerror = () => {
          // エラーはoncloseで処理するため、ここでは何もしない
        };

        if (isMounted) {
          setWs(newWs);
          wsInstance = newWs;
        }
      } catch (error) {
        if (retryCount.current < MAX_RETRY_COUNT) {
          retryCount.current++;
          setTimeout(connect, 5000);
        }
      }
    };

    connect();

    return () => {
      isMounted = false;
      clearInterval(heartbeat);
      if (wsInstance) {
        wsInstance.close();
      }
    };
  }, [url, getToken]);
  const sendMessage = useCallback(
    (message: string) => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(message);
      }
    },
    [ws]
  );

  const value = useMemo(() => ({ ws, sendMessage }), [ws, sendMessage]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("");
  }
  return context;
};
